import type {
  AcceptSeedingProposalServiceRequest,
  SeedingDeliveryAddressItem,
} from '@musinsa/services-curator';
import type { ResolvedSelectedOption } from './adapters/seedingProductOptionsAdapter';
import type { SeedingCampaignDetail, SeedingContentType } from './types';

/** 신청/수락 공용 request body (SeedingApplyAcceptBody). */
type SeedingMutationBody = AcceptSeedingProposalServiceRequest['body'];

interface BuildArgs {
  detail: SeedingCampaignDetail;
  /** 옵션 시트에서 해석 완료한 상품별 선택 (goodsNo 키). */
  selectedOptionsByGoodsNo: Record<number, ResolvedSelectedOption>;
  /** 29CM 배송지 변경에서 선택한 배송지. 무신사/미선택이면 undefined → detail.shippingAddress 사용. */
  selectedAddress?: SeedingDeliveryAddressItem;
  /** 동의한 필수 항목 코드 — 전송 시 4종 전체 필수(미전달이면 동의 미저장). */
  agreedConsents?: SeedingMutationBody['agreedConsents'];
  /** 동의를 진행한 화면 URL (감사용, ≤512자). */
  agreementPath?: SeedingMutationBody['agreementPath'];
  /**
   * 화면(PageView)에서 선택한 콘텐츠 유형. editable 캠페인은 `detail.contentType.selectedType`
   * 이 null 이므로(선택 상태는 PageView 로컬), 호출 측이 선택값을 주입한다. 미전달 시 detail 값 사용.
   */
  selectedContentType?: SeedingContentType;
  /**
   * 화면에서 입력한 브랜드 전달 메시지(free-text). editable 캠페인은 사용자 입력이
   * draft store 에만 있으므로 호출 측이 주입한다. 미전달 시 detail 값 사용. (SID-4157)
   */
  selectedMessage?: string | null;
}

export type BuildSeedingMutationBodyResult = { body: SeedingMutationBody } | { error: string };

/** 선택 배송지(29CM) 또는 상세의 표시 배송지(무신사/폴백) → payload 배송지. */
const buildDeliveryAddress = (
  detail: SeedingCampaignDetail,
  selectedAddress: SeedingDeliveryAddressItem | undefined,
): SeedingMutationBody['deliveryAddress'] => {
  if (selectedAddress) {
    return {
      deliveryAddressId: selectedAddress.id,
      addressName: selectedAddress.addressName,
      name: selectedAddress.name,
      phone: selectedAddress.phone,
      zipcode: selectedAddress.zipcode,
      address1: selectedAddress.address1,
      address2: selectedAddress.address2,
      memo: selectedAddress.memo,
    };
  }
  // 무신사 opener 는 id 만 반환 → 표시용 배송지(detail.shippingAddress)로 조립.
  // 0610 부터 detail 에 deliveryAddressId / zipcode 가 실려 폴백에도 id·우편번호 전달 가능.
  const shipping = detail.shippingAddress;
  return {
    deliveryAddressId: shipping.deliveryAddressId ?? null,
    addressName: shipping.addressName ?? null,
    name: shipping.recipient,
    phone: shipping.phone,
    zipcode: shipping.zipCode,
    address1: shipping.address,
    address2: shipping.addressDetail || null,
    memo: shipping.memo ?? null,
  };
};

/**
 * 시딩 신청/수락 공용 body 조립 + 사전 검증.
 * 옵션 미선택 / 콘텐츠 유형 미선택 / 배송지 미등록 시 사용자 메시지(error) 를 돌려준다.
 */
export const buildSeedingMutationBody = ({
  detail,
  selectedOptionsByGoodsNo,
  selectedAddress,
  agreedConsents,
  agreementPath,
  selectedContentType,
  selectedMessage,
}: BuildArgs): BuildSeedingMutationBodyResult => {
  // 품절 상품은 선택 대상에서 제외, 나머지는 옵션 선택 필수.
  // 아더컬러 슬롯은 멤버(색상) N개 중 1개만 선택하고, 선택은 그 멤버 goodsNo 로 키잉된다.
  // 대표 productId(첫 멤버)로만 검증하면 비-대표 색상 선택 시 false "미선택" 이 된다 →
  // 슬롯 멤버 기준으로 선택 여부를 판정·조립한다. (SID-4289)
  const sellableProducts = detail.products.filter((product) => !product.soldOut);
  const selectedOptions: ResolvedSelectedOption[] = [];
  for (const product of sellableProducts) {
    // 단품(옵션 없는 29CM 상품) — 옵션 선택 없이 goodsNo 만 전송(goodsOptionNo 미설정). (SID-4296/4314)
    if (product.isSingleProduct) {
      selectedOptions.push({ goodsNo: product.productId, extraOptionNos: [] });
      continue;
    }
    const selected = product.members
      .map((member) => selectedOptionsByGoodsNo[member.goodsNo])
      .find((option) => option !== undefined);
    if (!selected) {
      return { error: '상품 옵션을 모두 선택해 주세요.' };
    }
    selectedOptions.push(selected);
  }

  // editable 캠페인은 선택 상태가 PageView 로컬에만 있어 detail 엔 null → 호출 측 선택값 우선.
  const contentType = selectedContentType ?? detail.contentType.selectedType;
  if (!contentType) {
    return { error: '콘텐츠 유형을 선택해 주세요.' };
  }

  if (detail.shippingAddress.registered === false && !selectedAddress) {
    return { error: '배송지를 등록해 주세요.' };
  }

  return {
    body: {
      selectedOptions,
      contentType,
      deliveryAddress: buildDeliveryAddress(detail, selectedAddress),
      // 사용자 입력(draft store) 우선, 미전달 시 detail 원본. (SID-4157)
      messageToBrand: (selectedMessage ?? detail.contentType.message) || null,
      // 동의 필드는 전달된 경우에만 포함(미전달 = 전환기, BE 가 동의 미저장으로 처리).
      ...(agreedConsents ? { agreedConsents } : {}),
      ...(agreementPath ? { agreementPath } : {}),
    },
  };
};
